import { getDiffuseur } from '@/helpers/functions/diffuseur'
import { HTTP_HYPER_ESPACE_BACKEND } from '@/services/http-common'
import { NETWORKS_CAPTIONS } from '@/helpers/constantes/captionConstants'
import creerObjetCaption from '@/helpers/functions/captions'
import { buildSocialNetworkPatchPayload } from '@/helpers/functions/socialCaptionFlux'
import { Message } from 'element-ui'

export default {
  namespaced: true,
  state: {
    captions: [],
    message: null,
    isLoadingCaption: false,
    // Nouvel état pour le loading par network : { "diffuseur_postId": boolean }
    networkLoadingStates: {}
  },
  getters: {
    getCaptionByNetwork: (state) => (diffuseur, annonceId) => {
      const item = state.captions.find((caption) => caption.diffuseurNomDeCode === diffuseur && caption.annonceId === annonceId)
      return item
    },
    getMessage: (state) => {
      return state.message
    },
    getIsLoadingCaption: (state) => state.isLoadingCaption,
    // Nouveau getter pour vérifier le loading d'un network spécifique
    getIsLoadingCaptionByNetwork: (state) => (diffuseur, annonceId) => {
      const key = `${diffuseur}_${annonceId}`
      return state.networkLoadingStates[key] || false
    },
    // Getter pour vérifier si au moins un network est en loading
    getHasAnyNetworkLoading: (state) => {
      return Object.values(state.networkLoadingStates).some(isLoading => isLoading)
    }
  },
  actions: {
    loadCaptions ({ state, commit, rootState }, { universe, annonceId, bienVendu }) {
      commit('setIsLoadingCaption', true)
      const captions = []
      const captionsToGenerate = [] // Captions à générer automatiquement
      // Récupérer les captions existantes et créer l'objet pour les captions qui n'existent pas
      HTTP_HYPER_ESPACE_BACKEND.get(`/annonce/${annonceId}`)
        .then(response => {
          const post = response.data
          const donnees = response.data.donnees
          const sitimmoUser = rootState.app.sitimmoUser

          // Mettre à jour le store postManager avec les données récupérées
          commit('postManager/setCurrentPost', post, { root: true })

          // Déterminer le mode de fonctionnement
          const hasOptionIaOnly = sitimmoUser.optionIa && !sitimmoUser.personnalisationPrompts
          const hasFlux = !sitimmoUser.optionIa && !sitimmoUser.personnalisationPrompts
          const shouldAutoGenerate = hasOptionIaOnly || hasFlux

          for (const network in NETWORKS_CAPTIONS) {
            const diffuseur = getDiffuseur(network, universe, bienVendu)
            const captionKey = NETWORKS_CAPTIONS[network]
            let content = null

            // Vérifier si la caption existe dans donnees (structure avec .valeur)
            if (donnees && captionKey in donnees) {
              content = donnees[captionKey].valeur
            }
            // Sinon vérifier si la caption existe directement dans post
            else if (captionKey in post) {
              content = post[captionKey]
            }

            if (content) {
              // Cas 1 : La caption existe déjà
              const caption = creerObjetCaption(post.id, diffuseur, content)
              captions.push(caption)
            } else {
              // Cas 2 : La caption n'existe pas
              const caption = creerObjetCaption(post.id, diffuseur) // Texte vide par défaut
              captions.push(caption)

              // Si on doit générer automatiquement (optionIA seule ou flux)
              if (shouldAutoGenerate) {
                captionsToGenerate.push(caption)
              }
              // Si on est en perso prompts, on laisse vide et on attend l'action utilisateur
            }
          }

          // Commit des captions (avec texte existant ou vide selon les cas)
          commit('setCaptions', captions)

          // Générer automatiquement les captions manquantes si nécessaire
          if (shouldAutoGenerate && captionsToGenerate.length > 0) {
            captionsToGenerate.forEach(caption => {
              const diffuseurNomDeCode = caption.diffuseurNomDeCode
              const captionAnnonceId = caption.annonceId

              // Activer le loading pour ce network spécifique
              commit('setIsLoadingCaptionByNetwork', {
                diffuseur: diffuseurNomDeCode,
                postId: captionAnnonceId,
                isLoading: true
              })
              const url = `/annonce-post?annonceId=${captionAnnonceId}&diffuseurNomDeCode=${diffuseurNomDeCode}`
              HTTP_HYPER_ESPACE_BACKEND.get(url)
                .then(response => {
                  // Mettre à jour la caption avec le contenu généré
                  commit('setCaptionsEmpty', response.data)
                })
                .catch(error => {
                  console.log({ type: 'error', message: error })
                  Message.error(`Erreur lors de la génération automatique pour ${diffuseurNomDeCode}`)
                })
                .finally(() => {
                  // Désactiver le loading pour ce network spécifique
                  commit('setIsLoadingCaptionByNetwork', {
                    diffuseur: diffuseurNomDeCode,
                    postId: captionAnnonceId,
                    isLoading: false
                  })
                })
            })
          }
        })
        .catch((error) => {
          console.log(error)
          Message.error('Erreur de chargement des données')
        })
        .finally(() => {
          commit('setIsLoadingCaption', false)
        })
    },
    updateCaption ({ state, commit, rootState }, { caption, login, post, emission, isNeedSocialNetwork }) {
      const indexCaptionInStore = state.captions.findIndex((item) => item === caption)
      const nameCaptionNetwork = NETWORKS_CAPTIONS[caption.diffuseurNomDeCode.split('_')[0]]
      const universe = post.univers
        || (post.advertiser && post.advertiser.universeCode)
        || (rootState.app.sitimmoUser && rootState.app.sitimmoUser.univers)
      const fluxEmission = emission
        || post.emission
        || (post.sourceCode ? { codeName: post.sourceCode } : null)
      const socialFluxPayload = buildSocialNetworkPatchPayload(universe, isNeedSocialNetwork, fluxEmission)

      const data = {
        annonceur: {
          login: login
        },
        ...socialFluxPayload,
        annonce: {
          [nameCaptionNetwork]: caption.text
        }
      }
      // Gestion donnée url vidéo
      data.annonce.post_video_file_url_custom = post.post_video_file_url_custom

      // Activer le loading pour ce network spécifique
      commit('setIsLoadingCaptionByNetwork', {
        diffuseur: caption.diffuseurNomDeCode,
        postId: caption.annonceId,
        isLoading: true
      })

      HTTP_HYPER_ESPACE_BACKEND
        .patch(`/annonce/${post.id}`, data)
        .then(response => {
          commit('setCaptionOfNetwork', { indexCaptionInStore, caption })
          commit('setMessageCaptionUpdated', { type: 'success', message: 'Votre annonce a bien été mise à jour' })
        })
        .catch(error => {
          return { type: 'error', message: error }
        }).finally(() => {
        // Désactiver le loading pour ce network spécifique
          commit('setIsLoadingCaptionByNetwork', {
            diffuseur: caption.diffuseurNomDeCode,
            postId: caption.annonceId,
            isLoading: false
          })
        })
    },
    resetCaption ({ commit }, { network, universe, post, bienVendu }) {
      const annonceId = post.id
      const diffuseur = getDiffuseur(network, universe, bienVendu)
      const url = `/annonce-post?annonceId=${annonceId}&diffuseurNomDeCode=${diffuseur}`

      // Activer le loading pour ce network spécifique
      commit('setIsLoadingCaptionByNetwork', {
        diffuseur: diffuseur,
        postId: annonceId,
        isLoading: true
      })

      HTTP_HYPER_ESPACE_BACKEND.delete(url)
        .then((response) => {
          if (response.status === 200) {
            commit('deleteCaption', {
              diffuseur,
              annonceId
            })
            HTTP_HYPER_ESPACE_BACKEND.get(url)
              .then(response => {
                commit('setNewCaption', response.data)
              }).catch(error => {
                console.log({ type: 'error', message: error })
              }).finally(() => {
                // Désactiver le loading pour ce network spécifique
                commit('setIsLoadingCaptionByNetwork', {
                  diffuseur: diffuseur,
                  postId: annonceId,
                  isLoading: false
                })
              })
          }
        }).catch(error => {
        // Désactiver le loading en cas d'erreur
          commit('setIsLoadingCaptionByNetwork', {
            diffuseur: diffuseur,
            postId: annonceId,
            isLoading: false
          })
          return { type: 'error', message: error }
        })
    },
    loadCaptionsWithPromptLibrairies ({ state, commit }, { data, diffuseurName }) {
      const url = `/annonce-post?annonceId=${data.id}&diffuseurNomDeCode=${diffuseurName}`

      // Activer le loading pour ce network spécifique
      commit('setIsLoadingCaptionByNetwork', {
        diffuseur: diffuseurName,
        postId: data.id,
        isLoading: true
      })

      HTTP_HYPER_ESPACE_BACKEND.delete(url)
        .then(response => {
          if (response.status === 200) {
            const caption = { annonceId: data.id, diffuseurNomDeCode: diffuseurName }
            commit('deleteCaption', caption)
            HTTP_HYPER_ESPACE_BACKEND.get(url)
              .then((response) => {
                const indexCaption = state.captions.findIndex((caption) => caption.diffuseurNomDeCode === response.data.diffuseurNomDeCode)
                if (indexCaption > -1) {
                  commit('setCaptionOfNetwork', { index: indexCaption, caption: response.data })
                } else {
                  commit('setNewCaption', response.data)
                }
              }).catch(error => {
                console.log({ type: 'error', message: error })
              }).finally(() => {
                // Désactiver le loading pour ce network spécifique
                commit('setIsLoadingCaptionByNetwork', {
                  diffuseur: diffuseurName,
                  postId: data.id,
                  isLoading: false
                })
              })
          }
        }).catch(error => {
        // Désactiver le loading en cas d'erreur
          commit('setIsLoadingCaptionByNetwork', {
            diffuseur: diffuseurName,
            postId: data.id,
            isLoading: false
          })
          console.log({ type: 'error', message: error })
        })
    }
  },
  mutations: {
    setIsLoadingCaption (state, bool) {
      state.isLoadingCaption = bool
    },
    // Nouvelle mutation pour gérer le loading par network
    setIsLoadingCaptionByNetwork (state, { diffuseur, postId, isLoading }) {
      const key = `${diffuseur}_${postId}`
      if (isLoading) {
        state.networkLoadingStates = {
          ...state.networkLoadingStates,
          [key]: true
        }
      } else {
        const newState = { ...state.networkLoadingStates }
        delete newState[key]
        state.networkLoadingStates = newState
      }
    },
    setCaptions (state, payload) {
      state.captions = payload
    },
    setCaptionsEmpty (state, item) {
      const captionIndex = state.captions.findIndex((caption) => caption.diffuseurNomDeCode === item.diffuseurNomDeCode)
      if (captionIndex !== -1) {
        state.captions[captionIndex].text = item.text
      }
    },
    setCaptionOfNetwork (state, { index, caption }) {
      state.captions[index].text = caption.text
      state.captions[index].annonceId = caption.annonceId
      state.captions[index].diffuseurNomDeCode = caption.diffuseurNomDeCode
    },
    setNewCaption (state, newCaption) {
      const diffuseurNomDeCode = newCaption.diffuseurNomDeCode
      const exists = state.captions.some(annonce => annonce.diffuseurNomDeCode === diffuseurNomDeCode)
      if (!exists) {
        state.captions.push(newCaption)
      }
    },
    deleteCaption (state, { diffuseur, annonceId }) {
      // Find index la caption dans le store and delete
      const indexNetwork = state.captions.findIndex((caption) => typeof caption !== 'undefined' && caption.diffuseurNomDeCode === diffuseur && caption.annonceId === annonceId)
      // delete caption
      if (indexNetwork > -1) {
        state.captions.splice(indexNetwork, 1)
      }
    },
    setMessageCaptionUpdated (state, message) {
      state.message = message
      setTimeout(() => {
        state.message = null
      }, 2000)
    },
    cleanCaptions (state) {
      state.captions = []
      state.isLoadingCaption = false
      state.message = null
      // Nettoyer aussi les états de loading par network
      state.networkLoadingStates = {}
    },
    updateCaptionText (state, { diffuseur, postId, text }) {
      const captionIndex = state.captions.findIndex((caption) => {
        return caption.diffuseurNomDeCode === diffuseur && caption.annonceId === postId
      })
      if (captionIndex !== -1) {
        state.captions[captionIndex].text = text
      }
    }
  }
}
